import { supabase } from '@/integrations/supabase/client';

// Google Analytics 4 事件追踪工具

// 访客与会话标识
const VISITOR_ID_KEY = 'analytics_visitor_id';
const SESSION_ID_KEY = 'analytics_session_id';

const getVisitorId = (): string => {
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
};

const getSessionId = (): string => {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return 'session';
  }
};

const isInternalTraffic = (): boolean => {
  try {
    return (
      localStorage.getItem('internal_traffic') === 'true' ||
      window.location.hostname.includes('localhost') ||
      window.location.hostname.startsWith('127.')
    );
  } catch {
    return false;
  }
};

const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  const width = window.innerWidth || 0;
  return width < 768 ? 'mobile' : 'desktop';
};

// 定义事件类型
export interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

// 常用事件定义
export const GA_EVENTS = {
  // 页面浏览
  PAGE_VIEW: 'page_view',
  
  // 用户注册转化
  SIGN_UP_START: 'sign_up_start',
  SIGN_UP_COMPLETE: 'sign_up_complete',
  
  // 预约流程
  PRACTITIONER_VIEW: 'practitioner_view',
  BOOKING_START: 'booking_start',
  BOOKING_COMPLETE: 'booking_complete',
  PAYMENT_START: 'payment_start',
  PAYMENT_COMPLETE: 'payment_complete',
  
  // 搜索和筛选
  SEARCH_PRACTITIONERS: 'search_practitioners',
  FILTER_APPLIED: 'filter_applied',
  
  // 页面交互
  CLICK_CTA: 'click_cta',
  VIEW_CONTENT: 'view_content',
} as const;

// 检查GA是否可用
const isGAAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// 发送自定义事件
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!isGAAvailable()) {
    console.warn('Google Analytics not available');
    return;
  }

  try {
    const base = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      internal_traffic: isInternalTraffic(),
      device_type: getDeviceType(),
      timestamp: new Date().toISOString(),
    };

    window.gtag('event', eventName, {
      ...base,
      ...parameters,
    });

    console.log('GA Event tracked:', eventName, { ...base, ...parameters });
  } catch (error) {
    console.error('Error tracking GA event:', error);
  }
};

// 追踪页面浏览
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (!isGAAvailable()) return;

  try {
    window.gtag('config', 'G-49LDLCFYEN', {
      page_path: pagePath,
      page_title: pageTitle,
    });
    
    console.log('GA Page view tracked:', pagePath, pageTitle);
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// 追踪转化事件
export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: 'USD',
  });
};

// 追踪用户注册
export const trackSignUp = (method: string = 'email') => {
  trackEvent(GA_EVENTS.SIGN_UP_COMPLETE, {
    method,
  });
};

// 追踪预约开始
export const trackBookingStart = (practitionerId: string, serviceType?: string) => {
  trackEvent(GA_EVENTS.BOOKING_START, {
    practitioner_id: practitionerId,
    service_type: serviceType,
  });
};

// 追踪预约完成
export const trackBookingComplete = (practitionerId: string, bookingValue: number) => {
  trackEvent(GA_EVENTS.BOOKING_COMPLETE, {
    practitioner_id: practitionerId,
    value: bookingValue,
    currency: 'USD',
  });
  
  // 同时追踪为转化
  trackConversion('booking_complete', bookingValue);
};

// 追踪搜索行为
export const trackSearch = (searchTerm: string, resultsCount: number) => {
  trackEvent(GA_EVENTS.SEARCH_PRACTITIONERS, {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

// 追踪筛选使用
export const trackFilter = (filterType: string, filterValue: string) => {
  trackEvent(GA_EVENTS.FILTER_APPLIED, {
    filter_type: filterType,
    filter_value: filterValue,
  });
};

// 追踪CTA点击
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent(GA_EVENTS.CLICK_CTA, {
    cta_name: ctaName,
    location,
  });
};

// 追踪内容查看
export const trackContentView = (contentType: string, contentId: string) => {
  trackEvent(GA_EVENTS.VIEW_CONTENT, {
    content_type: contentType,
    content_id: contentId,
  });
};

// 为TypeScript声明gtag函数
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
